/**
 * Watchlist Data Models
 * Based on the Watchlist API Documentation
 */

import { createCompany } from './companyModels';

// Base API Response Model
export class ApiResponse {
  constructor(data = {}) {
    this.success = data.success || false;
    this.data = data.data || null;
    this.message = data.message || '';
    this.errorCode = data.error_code || null;
    if (data.notModified) this.notModified = true;
    if (data.etag != null) this.etag = data.etag;
    if (data.syncToken != null) this.syncToken = data.syncToken;
    if (data.legacy) this.legacy = true;
    if (data.pagination) this.pagination = data.pagination;
  }
}

// User Watchlist DTO
export class UserWatchlistDTO {
  constructor(data = {}) {
    this.userId = data.user_id || '';
    this.watchlistItems = (data.watchlist_items || []).map(item => new WatchlistItemDTO(item));
    this.createdDate = data.created_date || new Date().toISOString();
    this.lastUpdated = data.last_updated || new Date().toISOString();
    this.name = data.name || '';
    this.description = data.description || '';
    this.isPublic = data.is_public || false;
    this.maxItems = data.max_items || 100;
  }

  toJSON() {
    return {
      user_id: this.userId,
      watchlist_items: this.watchlistItems.map(item => item.toJSON()),
      created_date: this.createdDate,
      last_updated: this.lastUpdated,
      name: this.name,
      description: this.description,
      is_public: this.isPublic,
      max_items: this.maxItems
    };
  }
}

// Watchlist Item DTO - accept both snake_case (user_company_info) and camelCase (userCompanyInfo) from API.
// Backend soft-remove: when removed, item has removed: true and is excluded from list endpoints; re-add toggles removed back to false.
export class WatchlistItemDTO {
  constructor(data = {}) {
    this.companyId = data.company_id || data.companyId || '';
    this.addedDate = data.added_date || data.addedDate || new Date().toISOString();
    this.notes = data.notes || '';
    this.alertEnabled = data.alert_enabled !== undefined ? data.alert_enabled : (data.alertEnabled !== undefined ? data.alertEnabled : false);
    this.priority = data.priority || 'normal';
    this.portStartPoint = data.port_start_point || data.portStartPoint || '';
    this.portEndPoint = data.port_end_point || data.portEndPoint || '';
    this.removed = data.removed === true;
    const rawUserInfo = data.user_company_info ?? data.userCompanyInfo;
    this.userCompanyInfo = rawUserInfo && typeof rawUserInfo === 'object' ? new UserCompanyInfoDTO(rawUserInfo) : new UserCompanyInfoDTO();
  }

  toJSON() {
    return {
      company_id: this.companyId,
      added_date: this.addedDate,
      notes: this.notes,
      alert_enabled: this.alertEnabled,
      priority: this.priority,
      port_start_point: this.portStartPoint,
      port_end_point: this.portEndPoint,
      removed: this.removed,
      user_company_info: this.userCompanyInfo.toJSON()
    };
  }
}

// User Company Info DTO
export class UserCompanyInfoDTO {
  constructor(data = {}) {
    const raw = data.company_type || data.companyType || '';
    this.companyType = String(raw).trim() ? String(raw).trim().toLowerCase() : '';
    this.userAddresses = (data.user_addresses || []).map(addr => new UserAddressDTO(addr));
    this.routes = (data.routes || []).map(route => new RouteDTO(route));
    this.lastUpdated = data.last_updated || new Date().toISOString();
  }

  toJSON() {
    return {
      company_type: this.companyType,
      user_addresses: this.userAddresses.map(addr => addr.toJSON()),
      routes: this.routes.map(route => route.toJSON()),
      last_updated: this.lastUpdated
    };
  }
}

// User Address DTO
export class UserAddressDTO {
  constructor(data = {}) {
    this.addressId = data.address_id || '';
    this.streetAddress = data.street_address || '';
    this.city = data.city || '';
    this.stateProvince = data.state_province || '';
    this.postalCode = data.postal_code || '';
    this.country = data.country || '';
    this.addedDate = data.added_date || new Date().toISOString();
    this.isPrimary = data.is_primary || false;
  }

  toJSON() {
    return {
      address_id: this.addressId,
      street_address: this.streetAddress,
      city: this.city,
      state_province: this.stateProvince,
      postal_code: this.postalCode,
      country: this.country,
      added_date: this.addedDate,
      is_primary: this.isPrimary
    };
  }

  getFullAddress() {
    const parts = [
      this.streetAddress,
      this.city,
      this.stateProvince,
      this.postalCode,
      this.country
    ].filter(part => part && part.trim());
    
    return parts.join(', ');
  }

  getShortAddress() {
    const parts = [
      this.city,
      this.stateProvince,
      this.country
    ].filter(part => part && part.trim());
    
    return parts.join(', ');
  }
}

// Route DTO
export class RouteDTO {
  constructor(data = {}) {
    this.routeId = data.route_id || '';
    this.startPointAddressId = data.start_point_address_id || '';
    this.endPointAddressId = data.end_point_address_id || '';
    /** For inter-company routes: company ID where the end address lives */
    this.endPointCompanyId = data.end_point_company_id ?? data.endPointCompanyId ?? null;
    this.routeName = data.route_name || '';
    this.description = data.description || '';
    this.createdDate = data.created_date || new Date().toISOString();
    this.isActive = data.is_active !== undefined ? data.is_active : (data.isActive !== undefined ? data.isActive : true);
  }

  toJSON() {
    return {
      route_id: this.routeId,
      start_point_address_id: this.startPointAddressId,
      end_point_address_id: this.endPointAddressId,
      end_point_company_id: this.endPointCompanyId,
      route_name: this.routeName,
      description: this.description,
      created_date: this.createdDate,
      is_active: this.isActive
    };
  }
}

// Watchlist Statistics DTO
export class WatchlistStatisticsDTO {
  constructor(data = {}) {
    this.userId = data.user_id || '';
    this.totalCompanies = data.total_companies || 0;
    this.priorityDistribution = data.priority_distribution || {
      high: 0,
      normal: 0,
      low: 0
    };
    this.alertsEnabledCount = data.alerts_enabled_count || 0;
    this.lastUpdated = data.last_updated || new Date().toISOString();
  }

  toJSON() {
    return {
      user_id: this.userId,
      total_companies: this.totalCompanies,
      priority_distribution: this.priorityDistribution,
      alerts_enabled_count: this.alertsEnabledCount,
      last_updated: this.lastUpdated
    };
  }
}

// Watchlist Item with Company DTO
export class WatchlistItemWithCompanyDTO {
  constructor(data = {}) {
    const rawItem = data.watchlist_item ?? data.watchlistItem;
    this.watchlistItem = rawItem ? new WatchlistItemDTO(rawItem) : new WatchlistItemDTO();
    const rawCompany = data.company;
    this.company =
      rawCompany && typeof rawCompany === 'object' && Object.keys(rawCompany).length > 0
        ? createCompany(rawCompany)
        : null;
  }

  toJSON() {
    return {
      watchlist_item: this.watchlistItem.toJSON(),
      company: this.company?.toJSON?.() ?? this.company ?? null,
    };
  }
}

// Request DTOs

// Add to Watchlist Request
export class AddToWatchlistRequest {
  constructor(data = {}) {
    this.companyId = data.company_id || data.companyId || '';
    this.notes = data.notes || '';
    this.priority = data.priority || 'normal';
    this.portStartPoint = data.port_start_point || '';
    this.portEndPoint = data.port_end_point || '';
  }

  toJSON() {
    return {
      company_id: this.companyId,
      notes: this.notes,
      priority: this.priority,
      port_start_point: this.portStartPoint,
      port_end_point: this.portEndPoint
    };
  }

  validate() {
    const errors = [];
    
    
    if (!this.companyId || this.companyId.length > 32) {
      errors.push('Company ID is required and must be max 32 characters');
    }
    
    if (this.notes && this.notes.length > 500) {
      errors.push('Notes must be max 500 characters');
    }
    
    if (!['high', 'normal', 'low'].includes(this.priority)) {
      errors.push('Priority must be high, normal, or low');
    }
    
    if (this.portStartPoint && this.portStartPoint.length > 100) {
      errors.push('Port start point must be max 100 characters');
    }
    
    if (this.portEndPoint && this.portEndPoint.length > 100) {
      errors.push('Port end point must be max 100 characters');
    }
    
    return errors;
  }
}

// Update Watchlist Item Request
export class UpdateWatchlistItemRequest {
  constructor(data = {}) {
    this.notes = data.notes || '';
    this.priority = data.priority || 'normal';
    this.alertEnabled = data.alert_enabled !== undefined ? data.alert_enabled : false;
    this.portStartPoint = data.port_start_point || '';
    this.portEndPoint = data.port_end_point || '';
  }

  toJSON() {
    return {
      notes: this.notes,
      priority: this.priority,
      alert_enabled: this.alertEnabled,
      port_start_point: this.portStartPoint,
      port_end_point: this.portEndPoint
    };
  }

  validate() {
    const errors = [];
    
    if (this.notes && this.notes.length > 500) {
      errors.push('Notes must be max 500 characters');
    }
    
    if (!['high', 'normal', 'low'].includes(this.priority)) {
      errors.push('Priority must be high, normal, or low');
    }
    
    if (this.portStartPoint && this.portStartPoint.length > 100) {
      errors.push('Port start point must be max 100 characters');
    }
    
    if (this.portEndPoint && this.portEndPoint.length > 100) {
      errors.push('Port end point must be max 100 characters');
    }
    
    return errors;
  }
}

// Add User Address Request
export class AddUserAddressRequest {
  constructor(data = {}) {
    this.streetAddress = data.streetAddress || data.street_address || '';
    this.city = data.city || '';
    this.stateProvince = data.stateProvince || data.state_province || '';
    this.postalCode = data.postalCode || data.postal_code || '';
    this.country = data.country || '';
    this.isPrimary = data.isPrimary || data.is_primary || false;
  }

  toJSON() {
    return {
      streetAddress: this.streetAddress,
      city: this.city,
      stateProvince: this.stateProvince,
      postalCode: this.postalCode,
      country: this.country,
      isPrimary: this.isPrimary
    };
  }

  validate() {
    const errors = [];
    
    if (!this.streetAddress || this.streetAddress.length > 255) {
      errors.push('Street address is required and must be max 255 characters');
    }
    
    if (!this.city || this.city.length > 100) {
      errors.push('City is required and must be max 100 characters');
    }
    
    if (this.stateProvince && this.stateProvince.length > 50) {
      errors.push('State/Province must be max 50 characters');
    }
    
    if (this.postalCode && this.postalCode.length > 20) {
      errors.push('Postal code must be max 20 characters');
    }
    
    if (!this.country || this.country.length > 100) {
      errors.push('Country is required and must be max 100 characters');
    }
    
    return errors;
  }
}

// Add Route Request
export class AddRouteRequest {
  constructor(data = {}) {
    this.startPointAddressId = data.startPointAddressId || data.start_point_address_id || '';
    this.endPointAddressId = data.endPointAddressId || data.end_point_address_id || '';
    /** For inter-company routes: company ID where the end address lives (route stored under start company) */
    this.endPointCompanyId = data.endPointCompanyId ?? data.end_point_company_id ?? undefined;
    this.routeName = data.routeName || data.route_name || '';
    this.description = data.description || '';
    this.isActive = data.isActive !== undefined ? data.isActive : (data.is_active !== undefined ? data.is_active : true);
  }

  toJSON() {
    const body = {
      startPointAddressId: this.startPointAddressId,
      endPointAddressId: this.endPointAddressId,
      routeName: this.routeName,
      description: this.description,
      isActive: this.isActive
    };
    if (this.endPointCompanyId != null && this.endPointCompanyId !== '') {
      body.endPointCompanyId = this.endPointCompanyId;
    }
    return body;
  }

  validate() {
    const errors = [];
    
    if (!this.startPointAddressId) {
      errors.push('Start point address ID is required');
    }
    
    if (!this.endPointAddressId) {
      errors.push('End point address ID is required');
    }
    
    if (!this.routeName || this.routeName.length > 100) {
      errors.push('Route name is required and must be max 100 characters');
    }
    
    if (this.description && this.description.length > 500) {
      errors.push('Description must be max 500 characters');
    }
    
    return errors;
  }
}

// Update Route Status Request
export class UpdateRouteStatusRequest {
  constructor(data = {}) {
    this.isActive = data.isActive !== undefined ? data.isActive : true;
  }

  toJSON() {
    return {
      isActive: this.isActive
    };
  }

  validate() {
    const errors = [];
    
    if (typeof this.isActive !== 'boolean') {
      errors.push('isActive must be a boolean value');
    }
    
    return errors;
  }
}

// Company Type Enum (values are lowercase with underscores)
export const COMPANY_TYPES = {
  RAW_MATERIAL_SUPPLIER: 'raw_material_supplier',
  COMPONENT_MANUFACTURER: 'component_manufacturer',
  FINISHED_GOODS_MANUFACTURER: 'finished_goods_manufacturer',
  PACKAGING_COMPANY: 'packaging_company',
  EQUIPMENT_SUPPLIER: 'equipment_supplier',
  SERVICE_PROVIDER: 'service_provider',
  UTILITY_COMPANY: 'utility_company',
  LOGISTICS_COMPANY: 'logistics_company',
  CONTRACT_MANUFACTURER: 'contract_manufacturer',
  RESEARCH_DEVELOPMENT_COMPANY: 'research_development_company'
};

// Display labels for company types (single source of truth for UI)
export const COMPANY_TYPE_LABELS = {
  [COMPANY_TYPES.RAW_MATERIAL_SUPPLIER]: 'Raw Material Supplier',
  [COMPANY_TYPES.COMPONENT_MANUFACTURER]: 'Component Manufacturer',
  [COMPANY_TYPES.FINISHED_GOODS_MANUFACTURER]: 'Finished Goods Manufacturer',
  [COMPANY_TYPES.PACKAGING_COMPANY]: 'Packaging Company',
  [COMPANY_TYPES.EQUIPMENT_SUPPLIER]: 'Equipment Supplier',
  [COMPANY_TYPES.SERVICE_PROVIDER]: 'Service Provider',
  [COMPANY_TYPES.UTILITY_COMPANY]: 'Utility Company',
  [COMPANY_TYPES.LOGISTICS_COMPANY]: 'Logistics Company',
  [COMPANY_TYPES.CONTRACT_MANUFACTURER]: 'Contract Manufacturer',
  [COMPANY_TYPES.RESEARCH_DEVELOPMENT_COMPANY]: 'Research & Development',
  unassigned: 'Unassigned'
};

export const getCompanyTypeLabel = (type) => {
  if (!type) return COMPANY_TYPE_LABELS.unassigned;
  const normalized = String(type).trim().toLowerCase();
  return COMPANY_TYPE_LABELS[normalized] || normalized.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

// Priority Enum
export const PRIORITY_LEVELS = {
  HIGH: 'high',
  NORMAL: 'normal',
  LOW: 'low'
};

// Model Factory Functions
export const createApiResponse = (data) => new ApiResponse(data);
export const createUserWatchlist = (data) => new UserWatchlistDTO(data);
export const createWatchlistItem = (data) => new WatchlistItemDTO(data);
export const createUserCompanyInfo = (data) => new UserCompanyInfoDTO(data);
export const createUserAddress = (data) => new UserAddressDTO(data);
export const createRoute = (data) => new RouteDTO(data);
export const createWatchlistStatistics = (data) => new WatchlistStatisticsDTO(data);
export const createWatchlistItemWithCompany = (data) => new WatchlistItemWithCompanyDTO(data);

// Request Factory Functions
export const createAddToWatchlistRequest = (data) => new AddToWatchlistRequest(data);
export const createUpdateWatchlistItemRequest = (data) => new UpdateWatchlistItemRequest(data);
export const createAddUserAddressRequest = (data) => new AddUserAddressRequest(data);
export const createAddRouteRequest = (data) => new AddRouteRequest(data);
export const createUpdateRouteStatusRequest = (data) => new UpdateRouteStatusRequest(data);
